import { PermissionsGuard } from './shared/permissions/permissions.guard';
import { ORDER_ACCESS_IDENTIFIERS } from './shared/permissions/access-policy';
import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { AuthGuard } from '../@core/services/auth.guard';
import {
  INDICATOR_REPORTING_MODULE_ACCESS,
  INDICATOR_REPORTING_TRACKING_ACCESS,
} from './+indicator-reporting/shared/indicator-reporting-permissions';

const routes: Routes = [
  {
    path: 'verify/commitment/:agreementNumber',
    loadComponent: () =>
      import('./public/commitment-verification/commitment-verification.component').then(
        m => m.CommitmentVerificationComponent
      )
  },
  {
    path: 'verify/special-agreement/:agreementNumber',
    data: { agreementType: 'special' },
    loadComponent: () =>
      import('./public/commitment-verification/commitment-verification.component').then(
        m => m.CommitmentVerificationComponent
      )
  },
  {
    path: 'dashboard',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./dashboard/dashboard.module').then(m => m.DashboardModule)
  },
  {
    path: 'authentication',
    loadChildren: () =>
      import('./authentication/authentication.module').then(
        m => m.AuthenticationModule
      )
  },
  {
    path: 'lookup',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+lookup/lookup.module').then(
        m => m.LookupModule
      )
  },
  {
    path: 'user',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+user/user.module').then(
        m => m.UserModule
      )
  },
  {
    path: 'vendor',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+vendor/vendor.module').then(
        m => m.VendorModule
      )
  },
  {
    path: 'disease',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+disease/disease.module').then(
        m => m.DiseaseModule
      )
  },
  {
    path: 'data',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+data/data.module').then(
        m => m.DataModule
      )
  },

  {
    path: 'configuration',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+configuration/configuration.module').then(
        m => m.ConfigurationModule
      )
  },
  {
    path: 'product',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+product/product.module').then(
        m => m.ProductModule
      )
  },
  {
    path: 'laboratory',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+laboratory/laboratory.module').then(
        m => m.LaboratoryModule
      )
  },
  {
    path: 'testing',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+testing/testing.module').then(
        m => m.TestingModule
      )
  },
  {
    path: "payment",
    canLoad: [AuthGuard],
    canActivate: [AuthGuard, PermissionsGuard],
    data: { permissions: { only: ["system.admin"], redirectTo: "/" } },
    loadChildren: () =>
      import("./+payment/payment.module").then((m) => m.PaymentModule),
  },
  {
    path: "membership",
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import("./+membership/membership.module").then((m) => m.MembershipModule),
  },
  {
    path: 'CMS',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard, PermissionsGuard],
    data: { permissions: { only: ['content.manage'], redirectTo: '/' } },
    loadChildren: () =>
      import('./+CMS/CMS.module').then(
        m => m.CMSModule
      )
  },
  {
    path: 'program',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+program/program.module').then(
        m => m.ProgramModule
      )
  },
  {
    path: 'forecasting',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+forecasting/forecasting.module').then(
        m => m.ForecastingModule
      )
  },
  {
    path: 'import-feature',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+import-feature/import-feature.module').then(
        m => m.ImportFeatureModule
      )
  },
  {
    path: 'rdf',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+rdf/rdf.module').then(
        m => m.RDFModule
      )
  },
  {
    path: 'product-merge',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+product-merge/product-merge.module').then(
        m => m.ProductMergeModule
      )
  },
  {
    path: 'product-rollback',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+product-rollback/product-rollback.module').then(
        m => m.ProductRollbackModule
      )
  },
  {
    path: 'report',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+report/report.module').then(
        m => m.ReportModule
      )
  },
  {
    path: 'committed-demand',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+committed-demand/committed-demand.module').then(
        m => m.CommittedDemandModule
      )
  },
  {
    path: 'non-cdss-demand',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+non-cdss-demand/non-cdss-demand.module').then(
        m => m.NonCdssDemandModule
      )
  },
  {
    path: 'supply-planning',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+supply-planning/supply-planning.module').then(
        m => m.SupplyPlanningModule
      )
  },
  {
    path: 'indicator-reporting/submission-tracking',
    pathMatch: 'full',
    canLoad: [AuthGuard, PermissionsGuard],
    canActivate: [AuthGuard, PermissionsGuard],
    data: { permissions: { only: INDICATOR_REPORTING_TRACKING_ACCESS, redirectTo: '/' } },
    loadChildren: () =>
      import('./+indicator-reporting/submission-tracking/indicator-submission-tracking.module').then(
        m => m.IndicatorSubmissionTrackingModule
      )
  },
  {
    path: 'indicator-reporting',
    canLoad: [AuthGuard, PermissionsGuard],
    canActivate: [AuthGuard, PermissionsGuard],
    data: { permissions: { only: INDICATOR_REPORTING_MODULE_ACCESS, redirectTo: '/' } },
    loadChildren: () =>
      import('./+indicator-reporting/+indicator-reporting.module').then(
        m => m.IndicatorReportingModule
      )
  },
  {
    path: 'rrf-planning',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+rrf-planning/rrf-planning-page.module').then(
        m => m.RRFPlanningPageModule
      )
  },
  {
    path: 'orders',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard, PermissionsGuard],
    data: {
      permissions: {
        only: ORDER_ACCESS_IDENTIFIERS,
        redirectTo: '/launchpad'
      }
    },
    loadChildren: () =>
      import('./+orders/orders.module').then(
        m => m.OrdersModule
      )
  },
  {
    path: 'health-program-orders',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard, PermissionsGuard],
    data: {
      permissions: {
        only: ORDER_ACCESS_IDENTIFIERS,
        redirectTo: '/launchpad'
      }
    },
    loadChildren: () =>
      import('./+health-program-orders/health-program-orders.module').then(
        m => m.HealthProgramOrdersModule
      )
  },
  {
    path: 'received',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+received/received-product.module').then(
        m => m.ReceivedProductModule
      )
  },
  {
    path: 'stock',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+stock/stock.module').then(
        m => m.StockModule
      )
  },
  {
    path: 'issue',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+issue/issue.module').then(
        m => m.IssueModule
      )
  },
  {
    path: 'bulk-upload',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+bulk-upload/bulk-upload.module').then(
        m => m.BulkUploadModule
      )
  },
  {
    path: 'micro-learning',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./micro-learning/micro-learning.module').then(
        m => m.MicroLearningModule
      )
  },
  {
    path: 'program-monitoring',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+program-monitoring/+program-monitoring.module').then(
        (m) => m.ProgramMonitoringModule
      ),
  },
  {
    path: 'cdss',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+cdss/+cdss.module').then(
        m => m.CdssModule
      )
  },
  {
    path: 'launchpad',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+launchpad/launchpad.module').then(
        m => m.LaunchpadModule
      )
  },
  {
    path: 'community-feed',
    canLoad: [AuthGuard],
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./+community-feed/community-feed.module').then(
        m => m.CommunityFeedModule
      )
  },
  {
    path: 'program-forecast-tracking',
    children: [
      { path: '', redirectTo: '/launchpad', pathMatch: 'full' },
      { path: '**', redirectTo: '/launchpad' }
    ]
  },
  {
    path: 'program_forecast_tracking',
    children: [
      { path: '', redirectTo: '/launchpad', pathMatch: 'full' },
      { path: '**', redirectTo: '/launchpad' }
    ]
  },
  {
    path: 'program-tracker',
    children: [
      { path: '', redirectTo: '/launchpad', pathMatch: 'full' },
      { path: '**', redirectTo: '/launchpad' }
    ]
  },
  { path: 'index', redirectTo: '/launchpad', pathMatch: 'full' },
  {
    path: 'forlab',
    children: [
      { path: '', redirectTo: '/launchpad', pathMatch: 'full' },
      { path: 'index', redirectTo: '/launchpad', pathMatch: 'full' }
    ]
  },
  {
    path: '',
    redirectTo: '/launchpad',
    pathMatch: 'full'
  },
  { path: '**', redirectTo: '/authentication/signin', pathMatch: 'full' }
];
@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule { }
